import { FolderOpenOutlined, PlusOutlined } from '@ant-design/icons'
import { NetworkStatus } from '@apollo/client'
import { Button, Divider } from 'antd'
import { cloneDeep } from 'lodash'
import { remove } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  CareerProfileInput,
  useCareerProfileDeleteMutation,
  useCareerProfileUpdateMutation,
  useGetCareerProfileQuery,
} from '@/generated/graphql'

import { EditButton, ExperienceItem, createCareerItem } from '../profile-item.component'
import s from '../profile.module.less'
import { removeTypeName } from '../profile.util'

interface ProfileCareerPartComponentProps {
  userId: number
  isViewer: boolean
  isEdit: boolean
  onEdit: (isEdit: boolean) => void
}

export const WorkExperience: React.FC<ProfileCareerPartComponentProps> = ({ userId, isViewer, isEdit, onEdit }) => {
  const { t } = useTranslation()
  const [deleteIds, setDeleteIds] = React.useState<number[]>([])
  const [editedCareerProfile, setEditedCareerProfile] = React.useState<CareerProfileInput[]>()
  const { data, refetch, networkStatus } = useGetCareerProfileQuery({
    variables: {
      userId,
    },
    notifyOnNetworkStatusChange: true,
  })

  const [careerProfileUpdate, { loading: updateLoading }] = useCareerProfileUpdateMutation()

  const [careerProfileDelete] = useCareerProfileDeleteMutation()

  React.useEffect(() => {
    if (data) {
      const careerProfile = cloneDeep(data.user.careerProfile)
      removeTypeName(careerProfile)
      setEditedCareerProfile(careerProfile)
    }
  }, [isEdit])

  const loading = updateLoading || networkStatus === NetworkStatus.refetch
  const careerProfile = data?.user.careerProfile

  const getProfile = (isEdit: boolean) => (isEdit ? editedCareerProfile : careerProfile)

  const addArrayItem = () => {
    editedCareerProfile?.push(createCareerItem())
    setEditedCareerProfile(cloneDeep(editedCareerProfile))
  }

  const deleteArrayItem = (array: CareerProfileInput[], index: number) => {
    setDeleteIds((prev) => {
      const id = array[index].pastExperienceId
      if (id && prev.every((p) => p !== id)) {
        return [...prev, id]
      }
      return [...prev]
    })
    remove(array, (_, i) => i === index)
    setEditedCareerProfile(cloneDeep(array))
  }

  const header = (
    <div className={s.sectionHeadContainer}>
      <div className={s.sectionHead}>
        <FolderOpenOutlined className={s.sectionIcon} />
        <span className={s.sectionTitle}>{t('profile.workExperience.workExperienceInfo')}</span>
      </div>
      <div className={s.sectionHeadExtra}>
        <EditButton
          isViewer={isViewer}
          isEdit={isEdit}
          loading={loading}
          onEdit={onEdit}
          onUpdate={async () => {
            try {
              // delete
              if (deleteIds.length > 0) {
                for (const id of deleteIds) {
                  await careerProfileDelete({
                    variables: {
                      careerProfileId: id,
                    },
                  })
                }
              }

              // upsert
              if (editedCareerProfile) {
                for (const careerProfile of editedCareerProfile) {
                  await careerProfileUpdate({ variables: { careerProfile } })
                }
              }
              refetch()
            } catch {} // eslint-disable-line no-empty
            onEdit(false)
          }}
        />
      </div>
    </div>
  )

  const content = (
    <>
      <div>
        {getProfile(isEdit)?.map((workExperience, index) => (
          <div key={index}>
            <ExperienceItem
              index={index}
              isEdit={isEdit}
              root={getProfile(isEdit)}
              career={workExperience}
              deleteArrayItem={deleteArrayItem}
              setEditedCareerProfile={setEditedCareerProfile}
            />
            <Divider />
          </div>
        ))}
      </div>
      {isEdit && (
        <div style={{ marginRight: 16 }} className={s.buttonGroup}>
          <Button type='primary' icon={<PlusOutlined />} onClick={addArrayItem}>
            {t('profile.workExperience.addCompanyExperience')}
          </Button>
        </div>
      )}
    </>
  )

  return (
    <div className={s.section}>
      {header}
      <Divider className={s.sectionDivider} />
      {content}
    </div>
  )
}
